import { z } from "zod";
import type { SupersededProposalReceiptData } from "@/lib/actionRequests/proposalReceipt";
import { EXPERIMENT_ENTITY_PREFIX, isValidPrefixedKsuid } from "@/utils/ksuidGenerator";

const DATA_TRACKER_ENTITY_PREFIX = "dtk_";
const INSIGHT_ENTITY_PREFIX = "ins_";

const prefixedKsuid = (prefix: string, label: string) =>
  z.string().refine((id) => isValidPrefixedKsuid(id, prefix), {
    message: `Invalid ${label} public ID`,
  });

export const ObservationSchema = z
  .object({
    category: z.string().trim().min(1).max(80),
    content: z.string().trim().min(1).max(2000),
    confidence: z.number().int().min(1).max(5),
  })
  .strict();

/** Historical observation cards had no copy or collection-size caps. */
export const LegacyObservationSchema = z.object({
  category: z.string().min(1),
  content: z.string().min(1),
  confidence: z.number().int().min(1).max(5),
});

export const DraftInsightSchema = z
  .object({
    id: prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"),
    insight: z.string().min(1).max(1000),
    implications: z.string().max(2000).nullable(),
    confidence: z.number().int().min(1).max(5).nullable(),
  })
  .strict();

/** Historical draft cards allowed omitted context fields and uncapped copy. */
export const LegacyDraftInsightSchema = z.object({
  id: prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"),
  insight: z.string().min(1),
  implications: z.string().nullable().optional(),
  confidence: z.number().int().min(1).max(5).nullable().optional(),
});

export const InsightsDraftPayloadSchema = z
  .object({
    experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
    trackerPublicId: prefixedKsuid(DATA_TRACKER_ENTITY_PREFIX, "tracker"),
    trackerRowCount: z.number().int().min(0),
    observations: z.array(ObservationSchema).max(20),
    draftInsights: z.array(DraftInsightSchema).min(1).max(10),
  })
  .strict();

/** Historical cards can predate persisted observations and tracker-row projection. */
const LegacyInsightsDraftPayloadSchema = z.object({
  experimentPublicId: prefixedKsuid(EXPERIMENT_ENTITY_PREFIX, "experiment"),
  trackerPublicId: prefixedKsuid(DATA_TRACKER_ENTITY_PREFIX, "tracker"),
  trackerRowCount: z.number().int().min(0).optional(),
  observations: z.array(LegacyObservationSchema).optional(),
  draftInsights: z.array(LegacyDraftInsightSchema).min(1),
});

export const InsightsDraftResolveSchema = z
  .object({
    approvedInsightIds: z
      .array(prefixedKsuid(INSIGHT_ENTITY_PREFIX, "insight"))
      .min(1)
      .refine((ids) => new Set(ids).size === ids.length, {
        message: "approvedInsightIds must not contain duplicates",
      }),
    editedObservations: z.array(ObservationSchema).min(1).max(20).optional(),
  })
  .strict();

export type Observation = z.infer<typeof ObservationSchema>;
export type DraftInsight = z.infer<typeof DraftInsightSchema>;
export type LegacyDraftInsight = z.infer<typeof LegacyDraftInsightSchema>;
export type RenderableDraftInsight = DraftInsight | LegacyDraftInsight;
export type InsightsDraftPayload = z.infer<typeof InsightsDraftPayloadSchema>;
export type InsightsDraftResolve = z.infer<typeof InsightsDraftResolveSchema>;

export type InsightsDraftToolResult =
  | (InsightsDraftResolve & { toolCallId: string })
  | SupersededProposalReceiptData;

type InsightsDraftEntity = {
  type?: string;
  experimentPublicId?: string;
  trackerPublicId?: string;
  trackerRowCount?: number;
  observations?: Observation[];
  draftInsights?: RenderableDraftInsight[];
  toolInvoke?: { toolCallId?: string };
};

export type InsightsDraftArgs = {
  entities?: InsightsDraftEntity[];
  experimentPublicId?: string;
  trackerPublicId?: string;
  trackerRowCount?: number;
  observations?: Observation[];
  draftInsights?: RenderableDraftInsight[];
};

/**
 * Parses insights_draft card payload from assistant tool args (flat or entities[] shape).
 */
export function parseInsightsDraftPayload(
  args: unknown,
  toolCallId?: string,
  allowHistoricalReceipt = false,
) {
  const direct = InsightsDraftPayloadSchema.safeParse(args);
  if (direct.success) {
    return direct;
  }
  if (allowHistoricalReceipt) {
    const historicalDirect = LegacyInsightsDraftPayloadSchema.safeParse(args);
    if (historicalDirect.success) {
      return historicalDirect;
    }
  }

  const root = args as InsightsDraftArgs | null | undefined;
  const entities = root?.entities ?? [];
  const entity =
    entities.find(
      (item) =>
        item.type === "insights_draft" &&
        (!toolCallId || item.toolInvoke?.toolCallId === toolCallId),
    ) ?? entities.find((item) => item.type === "insights_draft");

  if (!entity) {
    return direct;
  }

  const candidate = {
    experimentPublicId: entity.experimentPublicId ?? root?.experimentPublicId,
    trackerPublicId: entity.trackerPublicId ?? root?.trackerPublicId,
    trackerRowCount: entity.trackerRowCount ?? root?.trackerRowCount,
    observations: entity.observations ?? root?.observations,
    draftInsights: entity.draftInsights ?? root?.draftInsights,
  };
  const currentEntity = InsightsDraftPayloadSchema.safeParse(candidate);
  if (currentEntity.success || !allowHistoricalReceipt) {
    return currentEntity;
  }
  return LegacyInsightsDraftPayloadSchema.safeParse(candidate);
}
